import { useCallback, useMemo } from 'react';

import { chartThemeTokens } from 'design-system';

import { useRegularOptionsWithAppearance } from '@/features/charts/hooks/options/useRegularOptions';
import getRegularTooltip from '@/features/charts/hooks/tooltip/getRegularTooltip';
import useForwardCurveTooltipFormatter from '@/features/charts/hooks/tooltip/useForwardCurveTooltipFormatter';
import usePrefersReducedMotion from '@/features/charts/hooks/usePrefersReducedMotion';
import { withForwardCurveSeriesAppearance } from '@/features/charts/lib/applyChartSeriesAppearance';
import {
  createChartAppearance,
  type ChartAppearance
} from '@/features/charts/theme/chartAppearance';
import type {
  ExtendedOptions,
  ExtendedSeriesOptionsType
} from '@/features/charts/types/highcharts';

import type { XAxisOptions } from 'highcharts';

/**
 * Configures X-axis options for forward curve charts.
 */
const configureXAxis = (xAxisOptions: XAxisOptions): void => {
  xAxisOptions.type = 'linear';
  xAxisOptions.tickInterval = 1;
  xAxisOptions.crosshair = true;

  // Remove formatter if present to use default behavior
  if (xAxisOptions.labels?.formatter) {
    xAxisOptions.labels.formatter = undefined;
  }
};

/**
 * Hook for creating Highcharts options for forward curve charts.
 * @param height - Optional chart height (multiplied by row height in base options)
 * @param preserveColors - When true, skips color assignment (used by seasonal charts)
 * @returns Object with createOptions function
 */
export const useForwardCurveOptionsWithAppearance = (
  height: number | undefined,
  preserveColors: boolean | undefined,
  appearance: ChartAppearance,
  prefersReducedMotion: boolean
) => {
  const { createOptions: createRegularOptions } = useRegularOptionsWithAppearance(
    height,
    false,
    appearance,
    prefersReducedMotion
  );
  const { createTooltipFormatter } = useForwardCurveTooltipFormatter(appearance);

  const tooltip = useMemo(
    () => getRegularTooltip(createTooltipFormatter, appearance, prefersReducedMotion).tooltip,
    [appearance, createTooltipFormatter, prefersReducedMotion]
  );

  const createOptions = useCallback(
    (chartSeries: ExtendedSeriesOptionsType[], hiddenSeriesIds: string[] = []): ExtendedOptions => {
      const coloredSeries = preserveColors
        ? chartSeries
        : chartSeries.map(series => withForwardCurveSeriesAppearance(series, appearance));

      const baseOptions = createRegularOptions(coloredSeries, hiddenSeriesIds);

      const options: ExtendedOptions = {
        ...baseOptions,
        navigator: { enabled: false },
        rangeSelector: { enabled: false }
      };

      // Configure X-axis if present (mutates only the cloned options object, not input)
      const xAxisOptions = options.xAxis;
      if (Array.isArray(xAxisOptions)) {
        xAxisOptions.forEach(configureXAxis);
      } else if (xAxisOptions) {
        configureXAxis(xAxisOptions);
      }

      return {
        ...options,
        xAxis: xAxisOptions,
        tooltip,
        chart: {
          ...options.chart,
          marginTop: 40,
          zooming: { type: undefined }
        },
        plotOptions: {
          ...options.plotOptions,
          series: {
            ...options.plotOptions?.series,
            dataLabels: {
              enabled: false
            }
          }
        }
      };
    },
    [appearance, createRegularOptions, preserveColors, tooltip]
  );

  return { createOptions };
};

const useForwardCurveOptions = (height?: number, preserveColors?: boolean) => {
  const mode = 'regular' as const;
  const appearance = useMemo(() => createChartAppearance(chartThemeTokens, mode), [mode]);
  const prefersReducedMotion = usePrefersReducedMotion();

  return useForwardCurveOptionsWithAppearance(
    height,
    preserveColors,
    appearance,
    prefersReducedMotion
  );
};

export default useForwardCurveOptions;
